import { postDataAPI } from './fetchData';

/**
 * Send an approval request to a team member
 * @param {Object} approvalData - The approval request data
 * @param {string} approvalData.approverId - ID of the user who should approve
 * @param {string} approvalData.title - Title of the approval request
 * @param {string} approvalData.description - Description of what needs approval
 * @param {string} approvalData.type - Type of approval ('booking', 'catalog', 'tour', 'financial', 'general')
 * @param {string} approvalData.priority - Priority level ('low', 'medium', 'high', 'urgent')
 * @param {string} approvalData.relatedEntityType - Type of related entity (e.g., 'booking', 'catalog_item')
 * @param {number} approvalData.relatedEntityId - ID of the related entity
 * @param {Date} approvalData.dueDate - Optional due date for the approval
 * @param {Object} approvalData.metadata - Additional metadata
 * @returns {Promise<Object>} - API response
 */
export const sendApprovalRequest = async (approvalData) => {
  try {
    console.log('🔍 sendApprovalRequest - Sending approval data:', approvalData);
    const token = JSON.parse(localStorage.getItem('dizeeToken'));
    console.log('🔍 sendApprovalRequest - Using token:', token ? 'Token exists' : 'No token');
    
    const response = await postDataAPI('user/approvals', approvalData, token);
    console.log('🔍 sendApprovalRequest - Full response:', response);
    console.log('🔍 sendApprovalRequest - Response data:', response.data);
    
    if (response.data?.success) {
      console.log('✅ sendApprovalRequest - Success!');
      return { success: true, data: response.data.data };
    } else {
      console.error('❌ sendApprovalRequest - Failed:', response.data?.message);
      return { success: false, error: response.data?.message || 'Failed to send approval request' };
    }
  } catch (error) {
    console.error('❌ sendApprovalRequest - Exception:', error);
    return { success: false, error: error.message || 'Failed to send approval request' };
  }
};

/**
 * Send a booking approval request
 * @param {Object} bookingData - The booking data that needs approval
 * @param {string} approverId - ID of the approver
 * @param {string} approverName - Name of the approver
 * @returns {Promise<Object>} - API response
 */
export const sendBookingApproval = async (bookingData, approverId, approverName) => {
  const approvalData = {
    approverId,
    title: `Booking Approval: ${bookingData.venue || bookingData.title || 'New Booking'}`,
    description: `Please review and approve this booking request for ${bookingData.venue || bookingData.title}. Date: ${bookingData.date || 'TBD'}, Location: ${bookingData.location || bookingData.city || 'TBD'}`,
    type: 'booking',
    priority: bookingData.priority || 'medium',
    relatedEntityType: 'booking',
    relatedEntityId: bookingData.id,
    dueDate: bookingData.approvalDeadline ? new Date(bookingData.approvalDeadline) : null,
    metadata: {
      bookingData,
      approverName,
      requestType: 'booking_approval',
      appType: 'booking',
      venue: bookingData.venue,
      date: bookingData.date,
      location: bookingData.location || bookingData.city,
      fee: bookingData.fee || bookingData.guarantee,
      capacity: bookingData.capacity
    }
  };

  return await sendApprovalRequest(approvalData);
};

/**
 * Send a catalog approval request
 * @param {Object} catalogData - The catalog item data that needs approval
 * @param {string} approverId - ID of the approver
 * @param {string} approverName - Name of the approver
 * @returns {Promise<Object>} - API response
 */
export const sendCatalogApproval = async (catalogData, approverId, approverName) => {
  const approvalData = {
    approverId,
    title: `Catalog Approval: ${catalogData.title || catalogData.songTitle || 'New Release'}`,
    description: `Please review and approve this catalog item: ${catalogData.title || catalogData.songTitle}. Artist: ${catalogData.artist || catalogData.artistName || 'TBD'}, Release Date: ${catalogData.releaseDate || 'TBD'}`,
    type: 'catalog',
    priority: catalogData.priority || 'medium',
    relatedEntityType: 'catalog_item',
    relatedEntityId: catalogData.id,
    dueDate: catalogData.approvalDeadline ? new Date(catalogData.approvalDeadline) : null,
    metadata: {
      catalogData,
      approverName,
      requestType: 'catalog_approval',
      appType: 'catalog',
      title: catalogData.title || catalogData.songTitle,
      artist: catalogData.artist || catalogData.artistName,
      releaseDate: catalogData.releaseDate,
      genre: catalogData.genre,
      label: catalogData.label
    }
  };

  return await sendApprovalRequest(approvalData);
};

/**
 * Send a tour manager approval request (flights, hotels, etc.)
 * @param {Object} tourData - The tour item data that needs approval
 * @param {string} approverId - ID of the approver
 * @param {string} approverName - Name of the approver
 * @param {string} itemType - Type of tour item ('flight', 'hotel', 'transport', 'crew', 'general')
 * @returns {Promise<Object>} - API response
 */
export const sendTourApproval = async (tourData, approverId, approverName, itemType = 'general') => {
  let title, description;
  
  switch (itemType) {
    case 'flight':
      title = `Flight Approval: ${tourData.departure || 'TBD'} to ${tourData.arrival || 'TBD'}`;
      description = `Please approve this flight booking: ${tourData.departure} to ${tourData.arrival} on ${tourData.date || 'TBD'}. Price: ${tourData.price || 'TBD'}`;
      break;
    case 'hotel':
      title = `Hotel Approval: ${tourData.name || tourData.hotelName || 'Hotel Booking'}`;
      description = `Please approve this hotel booking: ${tourData.name || tourData.hotelName} in ${tourData.location || tourData.city || 'TBD'}. Check-in: ${tourData.checkIn || 'TBD'}, Price: ${tourData.price || 'TBD'}`;
      break;
    case 'transport':
      title = `Transport Approval: ${tourData.type || 'Ground Transport'}`;
      description = `Please approve this transport booking: ${tourData.type || 'Ground Transport'} from ${tourData.pickup || 'TBD'} to ${tourData.destination || 'TBD'}`;
      break;
    default:
      title = `Tour Approval: ${tourData.title || 'Tour Item'}`;
      description = `Please review and approve this tour-related request: ${tourData.description || tourData.title || 'Tour item approval needed'}`;
  }

  const approvalData = {
    approverId,
    title,
    description,
    type: 'tour',
    priority: tourData.priority || 'medium',
    relatedEntityType: `tour_${itemType}`,
    relatedEntityId: tourData.id,
    dueDate: tourData.approvalDeadline ? new Date(tourData.approvalDeadline) : null,
    metadata: {
      tourData,
      approverName,
      requestType: 'tour_approval',
      appType: itemType,
      itemType,
      ...tourData
    }
  };

  return await sendApprovalRequest(approvalData);
};

/**
 * Get team members for task assignment (includes current user)
 * @returns {Promise<Array>} - Array of team members
 */
export const getTeamMembersForTasks = async () => {
  try {
    const token = JSON.parse(localStorage.getItem('dizeeToken'));
    const userArticle = JSON.parse(localStorage.getItem('dizeeUser'));
    const currentUserEmail = userArticle?.email;
    const currentUserId = userArticle?._id;
    
    const response = await postDataAPI('team/members', {
      userId: userArticle?._id,
      userRole: userArticle?.professionRole
    }, token);

    if (response.data?.status === 200) {
      const data = response.data.data || [];
      
      // Check if this is the artist view (flat array) or team member view (nested structure)
      if (data.length > 0 && data[0].artist) {
        // Team member view - flatten the structure and include artists
        const allMembers = [];
        
        data.forEach(artistGroup => {
          // Add the artist to the list (including current user)
          if (artistGroup.artist) {
            const artistName = artistGroup.artist.name || artistGroup.artist.email;
            const isCurrentUser = artistGroup.artist.email === currentUserEmail || artistGroup.artist._id === currentUserId;
            
            allMembers.push({
              _id: artistGroup.artist._id,
              name: isCurrentUser ? `${artistName} (me)` : artistName,
              email: artistGroup.artist.email,
              profilePicture: artistGroup.artist.profilePicture,
              role: 'artist',
              status: 'active',
              permissions: {
                canApprove: true,
                canEditContent: true,
                canViewAnalytics: true,
                canManageTeam: true,
                canViewFinancials: true,
                canEditProfile: true
              }
            });
          }
          
          // Add team members (including current user)
          if (artistGroup.members) {
            artistGroup.members.forEach(member => {
              const memberName = member.name || member.email;
              const isCurrentUser = member.email === currentUserEmail || member._id === currentUserId || member.teamMemberId === currentUserId;
              
              allMembers.push({
                ...member,
                name: isCurrentUser ? `${memberName} (me)` : memberName
              });
            });
          }
        });
        
        return allMembers;
      } else {
        // Artist view - include current user with "(me)" indicator
        return data.map(member => {
          const memberName = member.name || member.email;
          const isCurrentUser = member.email === currentUserEmail || member._id === currentUserId || member.teamMemberId === currentUserId;
          
          return {
            ...member,
            name: isCurrentUser ? `${memberName} (me)` : memberName
          };
        });
      }
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching team members for tasks:', error);
    return [];
  }
};

/**
 * Get team members for approval selection (excludes current user)
 * @returns {Promise<Array>} - Array of team members
 */
export const getTeamMembersForApproval = async () => {
  try {
    const token = JSON.parse(localStorage.getItem('dizeeToken'));
    const userArticle = JSON.parse(localStorage.getItem('dizeeUser'));
    const currentUserEmail = userArticle?.email;
    const currentUserId = userArticle?._id;
    
    const response = await postDataAPI('team/members', {
      userId: userArticle?._id,
      userRole: userArticle?.professionRole
    }, token);

    if (response.data?.status === 200) {
      const data = response.data.data || [];
      
      // Check if this is the artist view (flat array) or team member view (nested structure)
      if (data.length > 0 && data[0].artist) {
        // Team member view - flatten the structure and include artists
        const allMembers = [];
        
        data.forEach(artistGroup => {
          // Add the artist to the list (but exclude if it's the current user)
          if (artistGroup.artist) {
            const artistName = artistGroup.artist.name || artistGroup.artist.email;
            const isCurrentUser = artistGroup.artist.email === currentUserEmail || artistGroup.artist._id === currentUserId;
            
            // Don't add current user to approval list
            if (!isCurrentUser) {
              allMembers.push({
                _id: artistGroup.artist._id,
                name: artistName,
                email: artistGroup.artist.email,
                profilePicture: artistGroup.artist.profilePicture,
                role: 'artist',
                status: 'active',
                permissions: {
                  canApprove: true, // Artists can always approve
                  canEditContent: true,
                  canViewAnalytics: true,
                  canManageTeam: true,
                  canViewFinancials: true,
                  canEditProfile: true
                }
              });
            }
          }
          
          // Add team members (but exclude if it's the current user)
          if (artistGroup.members) {
            artistGroup.members.forEach(member => {
              const isCurrentUser = member.email === currentUserEmail || member._id === currentUserId || member.teamMemberId === currentUserId;
              
              // Don't add current user to approval list
              if (!isCurrentUser && (member.permissions?.canApprove || member.role === 'manager' || member.role === 'admin')) {
                const memberName = member.name || member.email;
                
                allMembers.push({
                  ...member,
                  name: memberName
                });
              }
            });
          }
        });
        
        return allMembers;
      } else {
        // Artist view - filter out current user and return team members who can approve
        return data
          .filter(member => {
            const isCurrentUser = member.email === currentUserEmail || member._id === currentUserId || member.teamMemberId === currentUserId;
            const canApprove = member.permissions?.canApprove || member.role === 'manager' || member.role === 'admin';
            
            // Exclude current user from approval list
            return !isCurrentUser && canApprove;
          })
          .map(member => ({
            ...member,
            name: member.name || member.email
          }));
      }
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching team members for approval:', error);
    return [];
  }
};

/**
 * Show approval selection modal (to be implemented in components)
 * @param {Object} itemData - The item data that needs approval
 * @param {string} itemType - Type of item ('booking', 'catalog', 'tour')
 * @param {Function} onApprovalSent - Callback when approval is sent
 */
export const showApprovalModal = (itemData, itemType, onApprovalSent) => {
  // This will be implemented as a React component
  // For now, we'll dispatch a custom event that components can listen to
  const event = new CustomEvent('showApprovalModal', {
    detail: { itemData, itemType, onApprovalSent }
  });
  window.dispatchEvent(event);
};
